import {
  Dropdown,
  FieldWrapper,
  FlexBox,
  InputText,
  SelectionList,
  Spinner,
  Text,
} from '@dixa/lib-shared-habla';
import { useArticleLinkLookup } from 'articleEditor/hooks/useArticleLinkLookup';
import {
  ArticleLinkLookupQuery,
  LanguageId,
} from 'lib-knowledge-management-common/graphql';
import {
  ChangeEvent,
  KeyboardEventHandler,
  useCallback,
  useRef,
  useState,
} from 'react';
import { useClickAway } from 'react-use';

import { ArticleItem } from './articleItem';
import s from './articleLinkLookup.module.scss';

type ArticleLookupProps = {
  collectionId: string | null;
  onSelect: (articleId: LookupArticle | null) => void;
  articleTitle: string | null;
  readOnly: boolean;
  languageId: LanguageId | undefined;
};

export type LookupArticle =
  ArticleLinkLookupQuery['articles']['query']['results'][0];

/* Renders a component for linking articles, providing a searchable dropdown interface. */
export const ArticleLinkLookup = ({
  collectionId,
  onSelect,
  articleTitle,
  readOnly,
  languageId,
}: ArticleLookupProps): JSX.Element => {
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isListActive, setIsListActive] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  const onSelectArticle = useCallback(
    (article: LookupArticle | null) => {
      onSelect(article);
      setIsListActive(false);
      setSearchTerm('');
    },
    [onSelect],
  );

  const { data, loading } = useArticleLinkLookup({
    skip: !isListActive,
    collectionId,
    searchValue: searchTerm,
    languageId,
  });
  useClickAway(containerRef, () => {
    setIsListActive(false);
  });

  const handleSearchChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  }, []);

  const onKeyDown: KeyboardEventHandler = useCallback((e) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      e.stopPropagation();
      setIsListActive(false);
      inputRef.current?.blur();
    }
  }, []);

  const inputValue =
    isListActive && !readOnly ? searchTerm : articleTitle || '';

  const items =
    data?.articles.query.results.map((a) => ({
      ...a,
      title: a.title || '',
      name: a.title || '',
    })) || [];

  return (
    <FieldWrapper className={s.fieldContainer} label="Article">
      <div ref={containerRef}>
        <InputText
          ref={inputRef}
          onFocus={() => setIsListActive(true)}
          readOnly={readOnly}
          value={inputValue}
          onChange={handleSearchChange}
          onKeyDown={onKeyDown}
          placeholder="Search for an article..."
          icon="angle-down"
        />
        {isListActive && !readOnly && (
          <ArticleSearchDropdown
            loading={loading}
            items={items}
            searchTerm={searchTerm}
            onSelect={onSelectArticle}
          />
        )}
      </div>
    </FieldWrapper>
  );
};

type ArticleSearchDropdownProps = {
  loading: boolean;
  items: LookupArticle[];
  searchTerm: string;
  onSelect: (article: LookupArticle | null) => void;
};

const ArticleSearchDropdown = ({
  loading,
  items,
  searchTerm,
  onSelect,
}: ArticleSearchDropdownProps): JSX.Element => {
  return (
    <Dropdown className={s.dropdownContainer} isOpen>
      {loading && (
        <FlexBox justifyContent="center" margin="400">
          <Spinner />
        </FlexBox>
      )}

      {!loading && (
        <>
          {items.length === 0 && searchTerm.length === 0 && (
            <FlexBox justifyContent="center" margin="400">
              <Text.Paragraph color="secondary">
                Search for an article
              </Text.Paragraph>
            </FlexBox>
          )}

          {items.length === 0 && searchTerm.length > 0 && (
            <FlexBox justifyContent="center" margin="400">
              <Text.Paragraph color="secondary">No results</Text.Paragraph>
            </FlexBox>
          )}

          {searchTerm.length > 0 && items.length > 0 && (
            <SelectionList<LookupArticle>
              isActive
              onChange={(e) => onSelect(e.selectedItem.value)}
              style={{
                width: '100%',
                overflow: 'auto',
              }}
              equalityFn={(a, b): boolean => a.id === b.id}
            >
              {items.map((article) => (
                <ArticleItem key={article.id} article={article} />
              ))}
            </SelectionList>
          )}
        </>
      )}
    </Dropdown>
  );
};
